// Hand-written companion to the auto-generated `client.ts`. Calls the Java self-sign-up-api
// directly via the connect nginx-backend rewrite (CPG-664).
//
// The relative path `/api/self-sign-up/venue/password-rules` resolves against the SPA's current
// origin (the connect domain, where treatwell-nginx-backend rewrites `/api/self-sign-up/` to the
// Java `/external-api/` path). The rules are language-agnostic — each carries a Maia CMS key the FE
// resolves to localised copy via i18n — so no language is sent.

import { useMock } from './utils';

export type PasswordRule = {
  ruleName: string;
  // Maia CMS key the FE resolves to localised copy for the active language (via i18n), keyed the
  // same as marketplace-iam so the copy is shared.
  maiaCmsKey: string;
  // Regex the password must match to satisfy this rule.
  pattern: string;
  // When true the rule is only surfaced while violated (e.g. the max-length cap), never as a
  // standing checklist item.
  hideWhenValid: boolean;
};

export type PasswordRulesResponse = {
  passwordRules: PasswordRule[];
};

// Built-in copy of the Connect policy, used before the fetch resolves and if it fails. It keeps the
// password step validating and showing its checklist at all times (fail-safe, not fail-open) — the
// endpoint replaces it with the authoritative rules as soon as they arrive, and the Java sign-up
// endpoint re-validates on submit regardless. Kept in sync with supplier-iam's PasswordRule set;
// drift only matters during the sub-second load window or a supplier-iam outage.
export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  {
    ruleName: 'PASSWORD_TOO_SHORT',
    maiaCmsKey: 'password.rules.error-messages.password-too-short',
    pattern: '^.{10,}$',
    hideWhenValid: false,
  },
  {
    ruleName: 'PASSWORD_UPPERCASE',
    maiaCmsKey: 'password.rules.error-messages.password-needs-uppercase',
    pattern: '.*[A-Z].*',
    hideWhenValid: false,
  },
  {
    ruleName: 'PASSWORD_LOWERCASE',
    maiaCmsKey: 'password.rules.error-messages.password-needs-lowercase',
    pattern: '.*[a-z].*',
    hideWhenValid: false,
  },
  {
    ruleName: 'PASSWORD_TOO_LONG',
    maiaCmsKey: 'password.rules.error-messages.password-too-long',
    pattern: '^.{0,64}$',
    hideWhenValid: true,
  },
];

export async function getPasswordRules(): Promise<PasswordRulesResponse> {
  if (useMock) {
    // Local mock runs use the same built-in policy as the fail-safe path, so the two can't drift.
    return { passwordRules: DEFAULT_PASSWORD_RULES };
  }
  try {
    const res = await fetch('/api/self-sign-up/venue/password-rules');
    if (res.ok) {
      const body = (await res.json()) as PasswordRulesResponse;
      if (body.passwordRules?.length) {
        return body;
      }
    }
  } catch {
    // fall through to the built-in policy below
  }
  // Fail-safe: never leave the password step without a policy. A transient blip or an empty/failed
  // response falls back to the built-in Connect rules rather than skipping client-side validation.
  return { passwordRules: DEFAULT_PASSWORD_RULES };
}
